/** Philippine formats (§1): ₱#,##0.00, DD MMM YYYY, Asia/Manila. */
import { format as formatDate } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

export const TIME_ZONE = 'Asia/Manila';

export function peso(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const amount = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(amount)) return '—';
  // The minus belongs in front of the sign: -₱25,000.00, not ₱-25,000.00.
  const digits = Math.abs(amount).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${amount < 0 ? '-' : ''}₱${digits}`;
}

/** Percentages carry one decimal (§7). Pass a ratio: 0.194 → "19.4%". */
export function percent(ratio: number | string | null | undefined): string {
  if (ratio === null || ratio === undefined || ratio === '') return '—';
  const value = typeof ratio === 'string' ? Number(ratio) : ratio;
  if (!Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

export function shortDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return formatDate(toZonedTime(date, TIME_ZONE), 'dd MMM yyyy');
}

export function dateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  return formatDate(toZonedTime(date, TIME_ZONE), 'dd MMM yyyy, h:mm a');
}

/** "3 days ago", "in 2 months" — for last-seen and due dates. */
export function relative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return formatter.format(Math.round(seconds / size), unit);
  }
  return formatter.format(seconds, 'second');
}

/** Days between two dates, ignoring the time of day, in Manila. */
export function daysBetween(from: string | Date, to: string | Date = new Date()): number {
  const start = toZonedTime(typeof from === 'string' ? new Date(from) : from, TIME_ZONE);
  const end = toZonedTime(typeof to === 'string' ? new Date(to) : to, TIME_ZONE);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.round((end.getTime() - start.getTime()) / 86_400_000);
}
